import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ShieldCheck, Loader2, Clock, CheckCircle2, PlayCircle, PauseCircle,
    RefreshCw, AlertTriangle, DollarSign, FileText, Info,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * FeeBreakdown — three-component price display for Snapshot pricing.
 *
 * Renders NOTHING when the breakdown status is not "confirmed".
 * Until Hudock (CA employment counsel) clears Q17 and Checkr confirms
 * the fee-collection permission in writing, every backend response
 * carries status="draft" and this component stays invisible. Flipping
 * env vars ``SNAPSHOT_FEE_BREAKDOWN_ENABLED=true`` + the six
 * SNAPSHOT_*_CENTS_* vars on the server switches it on — no frontend
 * deploy needed.
 *
 * Props:
 *   breakdown  — { status, checkr_cents, simonara_service_cents, third_party_estimate_cents }
 *   totalCents — package price_cents (used to sanity-check the sum)
 *   variant    — "card" (pre-checkout) | "row" (post-order)
 */
const FeeBreakdown = ({ breakdown, totalCents, variant = "card" }) => {
    if (!breakdown || breakdown.status !== "confirmed") return null;
    const checkr = Number(breakdown.checkr_cents || 0);
    const simonara = Number(breakdown.simonara_service_cents || 0);
    const thirdParty = Number(breakdown.third_party_estimate_cents || 0);
    const sum = checkr + simonara;
    if (sum <= 0 || (totalCents && sum !== totalCents)) return null;
    const fmt = (c) => `$${(c / 100).toFixed(2)}`;
    return (
        <div
            data-testid={`snapshot-fee-breakdown-${variant}`}
            className="mt-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.03] p-3 text-xs"
        >
            <div className="flex items-center justify-between text-muted-foreground">
                <span>Checkr screening fee</span>
                <span className="font-mono text-foreground">{fmt(checkr)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-muted-foreground">
                <span>Simonara screening service fee</span>
                <span className="font-mono text-foreground">{fmt(simonara)}</span>
            </div>
            {thirdParty > 0 && (
                <div className="mt-1 flex items-center justify-between text-muted-foreground italic">
                    <span>Court/database fees (at cost, if any)</span>
                    <span className="font-mono">up to {fmt(thirdParty)}</span>
                </div>
            )}
            <div className="mt-2 pt-2 border-t border-emerald-500/20 flex items-center justify-between font-semibold text-foreground">
                <span>Total today</span>
                <span className="font-mono">{fmt(sum)}</span>
            </div>
            <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
                Powered by Checkr. Total includes Checkr&rsquo;s screening fee and a separately disclosed
                Simonara screening service fee. Court, database, or third-party fees are passed through
                at cost with no markup and only billed if actually incurred.
            </p>
        </div>
    );
};

const STATUS_META = {
    consent_pending: { label: "Awaiting Your Authorization", icon: Clock, cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    payment_pending: { label: "Payment Required", icon: DollarSign, cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    ready_for_processing: { label: "Paid · Queued", icon: PlayCircle, cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
    in_progress: { label: "In Progress", icon: RefreshCw, cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
    completed: { label: "Completed", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    review_required: { label: "Under Review", icon: AlertTriangle, cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    cancelled: { label: "Cancelled", icon: PauseCircle, cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400" },
};
const StatusPill = ({ status }) => {
    const m = STATUS_META[status] || { label: status || "—", icon: Clock, cls: "bg-muted text-muted-foreground" };
    const Icon = m.icon;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${m.cls}`}>
            <Icon size={10} /> {m.label}
        </span>
    );
};

export default function PersonalBackgroundChecks() {
    const [rows, setRows] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [reviewing, setReviewing] = useState(null); // {req, disclosure}
    const [accept1, setAccept1] = useState(false);
    const [accept2, setAccept2] = useState(false);
    const [signature, setSignature] = useState("");
    const [params, setParams] = useSearchParams();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/talent/personal-background");
            setRows(data.results || []);
            setPackages(data.packages || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (params.get("paid")) {
            toast.success("Payment received — your personal background check is queued for processing.");
            setParams({});
        } else if (params.get("cancelled")) {
            toast.info("Payment cancelled. You can complete checkout anytime.");
            setParams({});
        }
    }, [params, setParams]);

    const start = async (pkgId) => {
        setBusy(true);
        try {
            // Hydrate candidate_location from the Passport so the
            // jurisdiction resolver picks the right disclosure template
            // (CA/NY/WA/Federal). Best-effort — missing city just falls
            // back to Federal FCRA copy which is still legally sound.
            let candidate_location = null;
            try {
                const { data: passport } = await api.get("/professional/passport");
                candidate_location = passport?.city || passport?.location || null;
            } catch { /* passport not built yet — federal fallback */ }
            await api.post("/talent/personal-background/start", {
                package_id: pkgId,
                candidate_location,
            });
            toast.success("Personal check started — review the disclosure to continue.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const openReview = async (req) => {
        setBusy(true);
        try {
            const { data } = await api.get(`/talent/personal-background/${req.id}/disclosure`);
            setReviewing({ req, disclosure: data });
            setAccept1(false); setAccept2(false); setSignature("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const submitAuthorize = async () => {
        if (!accept1 || !accept2 || !signature.trim()) return;
        setBusy(true);
        try {
            await api.post(`/talent/personal-background/${reviewing.req.id}/authorize`, {
                signature_name: signature.trim(),
                accept_disclosure: accept1,
                accept_authorization: accept2,
            });
            toast.success("Authorization recorded — pay to launch the check.");
            setReviewing(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    const startCheckout = async (req) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/talent/personal-background/${req.id}/checkout`);
            if (data?.url) window.location.href = data.url;
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="personal-bgc-page" className="space-y-6">
            <div>
                <p className="overline">Personal · For your own records</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Personal Background Check</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Run a background check <strong>on yourself, for your own records</strong>. This is intentionally
                    separate from any employer&apos;s hiring-stage screening — the result never becomes an employer
                    report, is not reused across employers, and does not appear on your Passport&apos;s Verified badges.
                </p>
                <div className="mt-3 rounded-lg border border-sky-500/40 bg-sky-500/[0.06] px-3 py-2 text-xs text-sky-800 dark:text-sky-300 inline-flex items-start gap-2">
                    <Info size={13} className="mt-0.5 shrink-0" />
                    <span>
                        <strong>What this is not:</strong> a substitute for the employer-specific background screening
                        a hiring company may ask you to authorize separately. Those follow their own FCRA workflow.
                    </span>
                </div>
            </div>

            {/* Package picker (only when the talent has no in-flight row) */}
            {!loading && rows.filter((r) => !["completed", "cancelled"].includes(r.status)).length === 0 && packages.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="personal-bgc-packages">
                    {packages.map((p) => (
                        <div key={p.id} data-testid={`personal-bgc-package-${p.id}`} className="card-soft p-5">
                            <h3 className="font-heading font-semibold text-foreground">{p.label}</h3>
                            <p className="mt-1 text-sm text-muted-foreground">{p.detail}</p>
                            <div className="mt-3 flex items-center justify-between">
                                <span className="font-heading font-semibold text-lg text-foreground">${(p.price_cents / 100).toFixed(2)}</span>
                                <button onClick={() => start(p.id)} disabled={busy} data-testid={`personal-bgc-start-${p.id}`} className="btn-green text-xs inline-flex items-center gap-1 disabled:opacity-60">
                                    <ShieldCheck size={11} /> Start
                                </button>
                            </div>
                            <FeeBreakdown breakdown={p.fee_breakdown} totalCents={p.price_cents} variant="card" />
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? null : (
                <div className="space-y-3">
                    <h2 className="font-heading font-semibold text-base text-foreground">Your personal checks</h2>
                    {rows.map((r) => (
                        <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`personal-bgc-row-${r.id}`} className="rounded-2xl border border-border bg-card p-5">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-semibold text-foreground">{r.package_label}</h3>
                                        <StatusPill status={r.status} />
                                    </div>
                                    <div className="mt-1.5 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                        <div className="inline-flex items-center gap-1"><Clock size={11} /> requested {new Date(r.requested_at).toLocaleString()}</div>
                                        <div className="inline-flex items-center gap-1"><DollarSign size={11} /> ${(r.price_cents / 100).toFixed(2)}</div>
                                        {r.consent_at && <div className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> authorized {new Date(r.consent_at).toLocaleString()}</div>}
                                        {r.paid_at && <div className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> paid {new Date(r.paid_at).toLocaleString()}</div>}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                    {r.status === "consent_pending" && (
                                        <button onClick={() => openReview(r)} disabled={busy} data-testid={`personal-bgc-review-${r.id}`} className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 inline-flex items-center gap-1"><ShieldCheck size={11} /> Review & Authorize</button>
                                    )}
                                    {r.status === "payment_pending" && (
                                        <button onClick={() => startCheckout(r)} disabled={busy} data-testid={`personal-bgc-pay-${r.id}`} className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 inline-flex items-center gap-1">
                                            {busy ? <Loader2 size={11} className="animate-spin" /> : <DollarSign size={11} />} Pay ${(r.price_cents / 100).toFixed(2)}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <FeeBreakdown breakdown={r.fee_breakdown} totalCents={r.price_cents} variant="row" />
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Review dialog */}
            <Dialog open={reviewing !== null} onOpenChange={(o) => { if (!o) setReviewing(null); }}>
                <DialogContent data-testid="personal-bgc-authorize-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Authorize your personal background check</DialogTitle>
                        <DialogDescription>{reviewing?.disclosure?.package_label}</DialogDescription>
                    </DialogHeader>
                    {reviewing && (
                        <div className="space-y-4 text-sm">
                            <div className="rounded-lg border border-amber-500/40 bg-amber-500/[0.08] px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                <strong>Legal notice:</strong> {reviewing.disclosure.legal_note}
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Disclosure ({reviewing.disclosure.disclosure_version})</div>
                                <p className="text-sm text-foreground whitespace-pre-wrap">{reviewing.disclosure.disclosure_text}</p>
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Authorization ({reviewing.disclosure.authorization_version})</div>
                                <p className="text-sm text-foreground whitespace-pre-wrap">{reviewing.disclosure.authorization_text}</p>
                            </div>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2">
                                    <Checkbox checked={accept1} onCheckedChange={() => setAccept1(!accept1)} data-testid="personal-bgc-accept-disclosure" className="!rounded-[4px] mt-0.5" />
                                    <span className="text-sm">I have read and understood the disclosure above.</span>
                                </label>
                                <label className="flex items-start gap-2">
                                    <Checkbox checked={accept2} onCheckedChange={() => setAccept2(!accept2)} data-testid="personal-bgc-accept-authorization" className="!rounded-[4px] mt-0.5" />
                                    <span className="text-sm">I authorize this check to be run on myself, for my own records.</span>
                                </label>
                                <div>
                                    <label className="text-xs font-semibold text-foreground">Type your full name as electronic signature</label>
                                    <Input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="personal-bgc-signature" placeholder="Your full name" />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <button type="button" onClick={() => setReviewing(null)} data-testid="personal-bgc-cancel" className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted">Cancel</button>
                        <button type="button" onClick={submitAuthorize} disabled={busy || !accept1 || !accept2 || !signature.trim()} data-testid="personal-bgc-authorize-submit" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                            {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} Authorize
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
